import React, { useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useSSE, NotificationData } from '../../hooks/useSSE';
import { useNotificationStore } from '../../stores/notificationStore';

const NotificationToast: React.FC = () => {
  const { token } = useAuthStore();
  const { fetchUnreadCount, fetchNotifications } = useNotificationStore();
  const [toasts, setToasts] = useState<NotificationData[]>([]);

  // 使用 SSE hook 监听通知事件
  useSSE({
    onNotification: (notification: NotificationData) => {
      setToasts((prev) => [...prev, notification]);

      if (token) {
        fetchUnreadCount(token);
        fetchNotifications(token, 1, 50);
      }

      // 5秒后自动移除通知
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== notification.id));
      }, 5000);
    },
  });

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'SYSTEM':
        return 'border-teal-500';
      case 'PAYMENT':
        return 'border-green-500';
      case 'AI':
        return 'border-emerald-500';
      default:
        return 'border-stone-500';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'SYSTEM':
        return (
          <div className="w-8 h-8 rounded-full bg-teal-500/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'PAYMENT':
        return (
          <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'AI':
        return (
          <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-stone-500/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
        );
    }
  };

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-20 right-4 z-[200] space-y-2 max-w-sm"
      role="region"
      aria-label="实时通知"
      aria-live="polite"
      aria-relevant="additions removals"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          aria-atomic="true"
          className={`card p-4 shadow-xl shadow-black/20 border-l-4 animate-slide-in ${getTypeColor(toast.type)}`}
        >
          <div className="flex items-start gap-3">
            {getTypeIcon(toast.type)}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                  {toast.title}
                </h4>
                <button
                  type="button"
                  onClick={() => removeToast(toast.id)}
                  aria-label={`关闭通知 ${toast.title}`}
                  className="text-stone-500 hover:text-stone-300 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                {toast.content}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationToast;
